import { FileTypeEnum, FileVisibilityEnum } from "@/enums";
import type { FileBucket, FileType, FileTypeConfig } from "@/types";

export const FILE_BUCKET_MERCHANT_PRIVATE = "merchant-files-private" as const;
export const FILE_BUCKET_MERCHANT_PUBLIC = "merchant-files-public" as const;
export const FILE_BUCKET_SYSTEM_PRIVATE = "system-files-private" as const;
export const FILE_BUCKET_SYSTEM_PUBLIC = "system-files-public" as const;

export const FILE_BUCKETS: readonly FileBucket[] = [
  FILE_BUCKET_MERCHANT_PRIVATE,
  FILE_BUCKET_MERCHANT_PUBLIC,
  FILE_BUCKET_SYSTEM_PRIVATE,
  FILE_BUCKET_SYSTEM_PUBLIC,
] as const;

export const FILE_MERCHANT_BUCKETS: ReadonlySet<FileBucket> = new Set([
  FILE_BUCKET_MERCHANT_PRIVATE,
  FILE_BUCKET_MERCHANT_PUBLIC,
]);

export const FILE_PUBLIC_BUCKETS: ReadonlySet<FileBucket> = new Set([
  FILE_BUCKET_MERCHANT_PUBLIC,
  FILE_BUCKET_SYSTEM_PUBLIC,
]);

export const FILE_IMAGE_CONFIG: FileTypeConfig = {
  allowedMimes: ["image/jpeg", "image/png", "image/webp"],
  allowedExtensions: [".jpg", ".jpeg", ".png", ".webp"],
  requiresSanitize: false,
  allowDirectUpload: true,
};

export const FILE_SVG_CONFIG: FileTypeConfig = {
  allowedMimes: ["image/svg+xml"],
  allowedExtensions: [".svg"],
  requiresSanitize: true,
  allowDirectUpload: true, // unified flow: sanitize at finalize, not before
};

export const FILE_DOCUMENT_CONFIG: FileTypeConfig = {
  allowedMimes: ["application/pdf"],
  allowedExtensions: [".pdf"],
  requiresSanitize: false,
  allowDirectUpload: true,
};

export const FILE_RESUMABLE_THRESHOLD_BYTES = 6 * 1024 * 1024;
export const FILE_MIN_SIZE_BYTES = 1;
export const FILE_FILENAME_MAX_LENGTH = 200;
export const FILE_FILENAME_ALLOWED_PATTERN = /^[a-zA-Z0-9._-]+$/;
export const FILE_SIGNED_URL_TTL_SECONDS = 60 * 60;
export const FILE_SIGNED_URL_TTL_MAX_SECONDS = 24 * 60 * 60;
export const FILE_SIGNED_UPLOAD_URL_TTL_SECONDS = 2 * 60 * 60;
export const FILE_TRASH_RETENTION_DAYS = 30;
export const FILE_DEFAULT_MAX_BYTES = 20 * 1024 * 1024; // 20 MiB

// File types supported by the v1 file storage layer. The DB enum currently
// only permits 'image' | 'svg' | 'document', so we narrow here too — audio
// and video stay in the enum for forward compat but are inactive paths.
export const SUPPORTED_FILE_TYPES = [
  FileTypeEnum.IMAGE,
  FileTypeEnum.SVG,
  FileTypeEnum.DOCUMENT,
] as const;

export type SupportedFileType = (typeof SUPPORTED_FILE_TYPES)[number];

export function getFileConfig(fileType: FileType): FileTypeConfig {
  switch (fileType) {
    case FileTypeEnum.IMAGE:
      return FILE_IMAGE_CONFIG;
    case FileTypeEnum.SVG:
      return FILE_SVG_CONFIG;
    case FileTypeEnum.DOCUMENT:
      return FILE_DOCUMENT_CONFIG;
    case FileTypeEnum.AUDIO:
    case FileTypeEnum.VIDEO:
      throw new Error(`file type ${fileType} not supported in v1`);
  }
}

export function getAcceptAttribute(
  fileTypes: readonly FileType[] = SUPPORTED_FILE_TYPES,
): string {
  return fileTypes
    .map((t) => getFileConfig(t).allowedMimes.join(","))
    .join(",");
}

// Resolve the file_type enum from a declared MIME against the supported set.
export function inferFileType(
  mime: string,
  fileTypes: readonly SupportedFileType[] = SUPPORTED_FILE_TYPES,
): SupportedFileType | null {
  for (const ft of fileTypes) {
    if (getFileConfig(ft).allowedMimes.includes(mime)) return ft;
  }
  return null;
}

// Image and svg default to public — they're meant to render in wallet
// passes, marketing pages, etc. Documents (PDF) default to private.
export function defaultVisibilityFor(
  fileType: SupportedFileType,
): FileVisibilityEnum {
  switch (fileType) {
    case FileTypeEnum.IMAGE:
    case FileTypeEnum.SVG:
      return FileVisibilityEnum.PUBLIC;
    case FileTypeEnum.DOCUMENT:
      return FileVisibilityEnum.PRIVATE;
  }
}
